import { useState, useEffect, useCallback } from "react";
import { db } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { useTenancy } from "@/contexts/TenancyContext";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { generateMeetingAgenda } from "@/js/external-js/variableModules/utils/generateMeetingAgenda.util";
const useMeetingAgenda = () => {
  const { activeTenancy, userIdToName } = useTenancy();
  const { id: companyId, legalName: companyName } = activeTenancy;
  const normalizeCompanyName = (text) => {
    if (!text || !companyName) return text;
    const acmeRegex = /acme\s*,?\s*inc\.?/gi;
    return text.replace(acmeRegex, companyName);
  };
  const [assuranceItems, setAssuranceItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reviewPeriod, setReviewPeriod] = useState("3");
  const [isExporting, setIsExporting] = useState(false);
  const extractAssIDNumber = (assID) => {
    if (!assID) return 0;
    const match = assID.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };
  const fetchAssuranceItems = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const assuranceRef = doc(db, "customers", companyId, "compliance", "assurance");
      const assuranceDoc = await getDoc(assuranceRef);
      if (!assuranceDoc.exists()) {
        throw new Error("Assurance document not found");
      }
      const data = assuranceDoc.data();
      const currentDate = /* @__PURE__ */ new Date();
      const pastDate = /* @__PURE__ */ new Date();
      pastDate.setMonth(pastDate.getMonth() - parseInt(reviewPeriod, 10));
      const items = [];
      for (const itemKey in data) {
        const item = data[itemKey];
        if (item?.checkDetail?.active === "true") {
          const nextCheck = item.checkFrequency.nextCheck.toDate();
          const checkDaysFB = parseInt(item.checkFrequency.checkDays || "0", 10);
          const lastCheck = new Date(nextCheck);
          lastCheck.setDate(lastCheck.getDate() - checkDaysFB);
          if (nextCheck >= pastDate && nextCheck <= currentDate || lastCheck >= pastDate && lastCheck <= currentDate) {
            for (const historyKey in item.checkHistory) {
              const historyItem = item.checkHistory[historyKey];
              if (historyItem.check) {
                items.push({
                  item: {
                    ...item,
                    checkHistory: { [historyKey]: historyItem },
                    assignedTo: item.checkDetail.assignedTo
                  },
                  taskID: item.assID,
                  name: normalizeCompanyName(item.checkDetail.check),
                  nextCheck
                });
              }
            }
          }
        }
      }
      const uniqueItems = Array.from(new Map(items.map((item) => [item.taskID, item])).values());
      uniqueItems.sort((a, b) => {
        const statusA = a.item.checkDetail.active?.toLowerCase() || "";
        const statusB = b.item.checkDetail.active?.toLowerCase() || "";
        if (statusA === statusB) {
          const numA = extractAssIDNumber(a.taskID);
          const numB = extractAssIDNumber(b.taskID);
          return numA - numB;
        }
        return statusA === "incomplete" ? -1 : 1;
      });
      setAssuranceItems(uniqueItems);
    } catch (err) {
      console.error("Error fetching assurance items:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch assurance items"));
    } finally {
      setIsLoading(false);
    }
  }, [companyId, reviewPeriod]);
  const generateHtmlContent = useCallback(() => {
    const formatDate = (date) => {
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      }).format(date);
    };
    const getStatusColor = (status) => {
      const statusLower = status?.toLowerCase() || "";
      if (statusLower.includes("complete") || statusLower.includes("done") || statusLower.includes("approved") || statusLower.includes("active")) {
        return "background-color: #dcfce7; color: #166534;";
      } else if (statusLower.includes("progress") || statusLower.includes("pending") || statusLower.includes("incomplete")) {
        return "background-color: #fef9c3; color: #854d0e;";
      } else if (statusLower.includes("overdue") || statusLower.includes("late") || statusLower.includes("inactive")) {
        return "background-color: #fee2e2; color: #991b1b;";
      }
      return "background-color: #f3f4f6; color: #4b5563;";
    };
    const itemsHtml = assuranceItems.map((item) => `
      <div style="display: flex; margin-bottom: 12px; padding: 16px; border: 1px solid #e2e8f0; border-radius: 6px;">
        <div style="width: 96px; flex-shrink: 0;">
          <span style="color: #64748b; font-family: monospace; font-size: 14px; word-break: break-word;">
            ${item.item.assID || "N/A"}
          </span>
        </div>
        <div style="flex: 1; min-width: 0;">
          <p style="font-weight: 500; color: #1e293b; margin: 0 0 8px 0; word-break: break-word;">
            ${item.name}
          </p>
          <div style="display: flex; flex-wrap: wrap; gap: 12px; font-size: 14px; color: #64748b;">
            <span style="padding: 4px 10px; border-radius: 9999px; font-size: 12px; font-weight: 500; ${getStatusColor(item.item.checkDetail.active ? "active" : "inactive")}">
              ${item.item.checkDetail.active ? "Active" : "Inactive"}
            </span>
            ${item.item.assignedTo ? `
              <span style="display: flex; align-items: center; white-space: nowrap;">
                <span style="color: #94a3b8;">Assigned to</span>
                <span style="font-weight: 500; color: #1e293b; margin-left: 4px; overflow: hidden; text-overflow: ellipsis; max-width: 120px;">
                  ${userIdToName[item.item.assignedTo]}
                </span>
              </span>
            ` : ""}
            ${item.nextCheck ? `
              <span style="display: flex; align-items: center; white-space: nowrap;">
                <span style="color: #94a3b8;">Due</span>
                <span style="font-weight: 500; color: #1e293b; margin-left: 4px;">
                  ${formatDate(item.nextCheck)}
                </span>
              </span>
            ` : ""}
          </div>
        </div>
      </div>
    `).join("");
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Meeting Agenda - ${(/* @__PURE__ */ new Date()).toLocaleDateString()}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 24px; color: #1e293b; max-width: 800px; margin: 0 auto; }
            h1 { color: #1e40af; margin: 0 0 24px 0; font-size: 24px; }
            .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
            .period { color: #64748b; font-size: 14px; }
            .divider { height: 1px; background: #e2e8f0; margin: 24px 0; }
            .items-container { margin-top: 16px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Meeting Agenda</h1>
            <div class="period">Review Period: ${reviewPeriod} Months</div>
          </div>
          <div class="divider"></div>
          <div class="items-container">
            ${itemsHtml}
          </div>
        </body>
      </html>
    `;
  }, [assuranceItems, reviewPeriod]);
  const exportToPdf = async () => {
    if (!companyId) return false;
    try {
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              margin: 0;
              padding: 20px;
              color: #1a1a1a;
              background-color: #ffffff;
              line-height: 1.5;
            }
            .header {
              margin-bottom: 24px;
              padding-bottom: 12px;
              border-bottom: 1px solid #e2e8f0;
            }
            
            .section-title {
              font-size: 18px;
              font-weight: 600;
              color: #1a1a1a;
              margin: 24px 0 12px 0;
              padding-bottom: 6px;
              border-bottom: 1px solid #e2e8f0;
            }
            
            .agenda-section {
              margin-bottom: 24px;
            }
            
            .agenda-items {
              margin-left: 16px;
              padding-left: 8px;
              border-left: 2px solid #e2e8f0;
            }
            
            .agenda-items p {
              margin: 8px 0;
              line-height: 1.5;
            }
            .header h1 {
              margin: 0 0 8px 0;
              font-size: 24px;
              color: #1a1a1a;
            }
            .header p {
              margin: 0;
              color: #4b5563;
            }
            .item {
              display: flex;
              margin-bottom: 12px;
              padding: 16px;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              background-color: #ffffff;
            }
            .item-id {
              width: 96px;
              flex-shrink: 0;
            }
            .item-id span {
              color: #64748b;
              font-family: monospace;
              font-size: 14px;
              word-break: break-word;
            }
            .item-content {
              flex: 1;
              min-width: 0;
            }
            .item-title {
              font-weight: 500;
              color: #1a1a1a;
              margin: 0 0 8px 0;
              word-break: break-word;
            }
            .item-meta {
              display: flex;
              flex-wrap: wrap;
              gap: 12px;
              font-size: 14px;
              color: #4b5563;
            }
            .status {
              padding: 4px 10px;
              border-radius: 9999px;
              font-size: 12px;
              font-weight: 500;
            }
            .status-active {
              background-color: #dcfce7;
              color: #166534;
            }
            .status-inactive {
              background-color: #fee2e2;
              color: #991b1b;
            }
            .meta-item {
              display: flex;
              align-items: center;
              white-space: nowrap;
            }
            .meta-label {
              color: #6b7280;
            }
            .meta-value {
              font-weight: 500;
              color: #1a1a1a;
              margin-left: 4px;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 120px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${activeTenancy?.name} - ISO Committee Meeting</h1>
            <p>Generated on ${(/* @__PURE__ */ new Date()).toLocaleDateString()}</p>
          </div>
          
          <section class="agenda-section">
            <h2 class="section-title">Meeting Agenda</h2>
            <div class="agenda-items">
              <p>Status of actions from the previous meeting</p>
              <p>Discussion on any changes to external or internal issues that are relevant to the management system</p>
              <p>Feedback on management system performance</p>
              <p>Review of any nonconformities and corrective actions</p>
              <p>Review of monitoring and management system metrics</p>
              <p>Review of any audit results</p>
              <p>Review of management system objectives and their fulfilment</p>
              <p>Review of any feedback from interested parties</p>
              <p>Status of risks, including any updates to Risk Assessments</p>
            </div>
          </section>
          
          <section class="assurance-section">
            <h2 class="section-title">Assurance Items</h2>
          ${assuranceItems.map((item) => `
            <div class="item">
              <div class="item-id">
                <span>${item.item.assID || "N/A"}</span>
              </div>
              <div class="item-content">
                <p class="item-title">${item.name}</p>
                <div class="item-meta">
                  <span class="status ${item.item.checkDetail?.active ? "status-active" : "status-inactive"}">
                    ${item.item.checkDetail?.active ? "Active" : "Inactive"}
                  </span>
                  ${item.item.assignedTo ? `
                    <span class="meta-item">
                      <span class="meta-label">Assigned to</span>
                      <span class="meta-value">${userIdToName[item.item.assignedTo] || "Unassigned"}</span>
                    </span>
                  ` : ""}
                  ${item.nextCheck ? `
                    <span class="meta-item">
                      <span class="meta-label">Due</span>
                      <span class="meta-value">${new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      }).format(item.nextCheck)}</span>
                    </span>
                  ` : ""}
                </div>
              </div>
            </div>
          `).join("")}
        </body>
        </html>
      `;
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.width = "800px";
      iframe.style.left = "-9999px";
      iframe.style.top = "0";
      iframe.style.border = "none";
      document.body.appendChild(iframe);
      return new Promise((resolve) => {
        iframe.onload = async () => {
          try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!iframeDoc) {
              throw new Error("Could not access iframe document");
            }
            iframeDoc.open();
            iframeDoc.write(htmlContent);
            iframeDoc.close();
            await new Promise((resolve2) => setTimeout(resolve2, 500));
            const canvas = await html2canvas(iframeDoc.body, {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              scrollX: 0,
              scrollY: 0,
              windowWidth: 800,
              windowHeight: iframeDoc.body.scrollHeight,
              backgroundColor: "#ffffff"
            });
            document.body.removeChild(iframe);
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({
              orientation: "portrait",
              unit: "mm",
              format: "a4"
            });
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
            const pdfHeight = imgProps.height * pdfWidth / imgProps.width;
            pdf.addImage(imgData, "PNG", 10, 10, pdfWidth, pdfHeight);
            pdf.save(`meeting-agenda-${(/* @__PURE__ */ new Date()).toISOString().split("T")[0]}.pdf`);
            resolve(true);
          } catch (err) {
            console.error("Error in PDF generation:", err);
            document.body.removeChild(iframe);
            resolve(false);
          }
        };
        iframe.srcdoc = "<html><body>Loading...</body></html>";
      });
    } catch (err) {
      console.error("Error exporting to PDF:", err);
      setError(err instanceof Error ? err : new Error("Failed to export to PDF"));
      return false;
    }
  };
  const exportToWord = async () => {
    if (!companyId) return false;
    try {
      await generateMeetingAgenda(companyId, parseInt(reviewPeriod));
      return true;
    } catch (err) {
      console.error("Error exporting to Word:", err);
      setError(err instanceof Error ? err : new Error("Failed to export to Word"));
      return false;
    }
  };
  const exportAgenda = async (format = "word") => {
    if (!companyId) return false;
    setIsExporting(true);
    try {
      if (format === "pdf") {
        return await exportToPdf();
      } else {
        return await exportToWord();
      }
    } catch (err) {
      console.error("Error exporting agenda:", err);
      setError(err instanceof Error ? err : new Error("Failed to export agenda"));
      return false;
    } finally {
      setIsExporting(false);
    }
  };
  useEffect(() => {
    if (companyId) {
      fetchAssuranceItems();
    }
  }, [companyId, reviewPeriod, fetchAssuranceItems]);
  return {
    assuranceItems,
    isLoading,
    error,
    reviewPeriod,
    setReviewPeriod,
    exportAgenda,
    isExporting,
    refresh: fetchAssuranceItems,
    generateHtmlContent
    // Exposed for testing or future use
  };
};
export {
  useMeetingAgenda
};
